import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import './PdfViewerModal.css';

export default function PdfViewerModal({ url, fileName, onClose }) {
    const name = fileName || 'document.pdf';
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let revoke = null;
        setLoading(true);
        setError(false);

        fetch(url)
            .then((resp) => {
                if (!resp.ok) throw new Error('fetch failed');
                return resp.blob();
            })
            .then((blob) => {
                const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                const objectUrl = URL.createObjectURL(pdfBlob);
                revoke = objectUrl;
                setBlobUrl(objectUrl);
                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });

        return () => {
            if (revoke) URL.revokeObjectURL(revoke);
        };
    }, [url]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    const handleDownload = useCallback(() => {
        const a = document.createElement('a');
        a.href = blobUrl || url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [blobUrl, url, name]);

    if (!url) return null;

    return ReactDOM.createPortal(
        <div
            className="pdf-viewer-modal"
            onClick={onClose}
        >
            <div
                className="pdf-viewer-modal__header"
                onClick={(e) => e.stopPropagation()}
            >
                <span className="pdf-viewer-modal__title">
                    {name}
                </span>

                <div className="pdf-viewer-modal__actions">
                    <button
                        onClick={handleDownload}
                        disabled={loading}
                        className={`pdf-viewer-modal__download${loading ? ' pdf-viewer-modal__download--loading' : ''}`}
                        title="Download PDF"
                    >
                        <span className="material-symbols-outlined pdf-viewer-modal__download-icon">
                            download
                        </span>
                        Download
                    </button>

                    <button
                        onClick={onClose}
                        className="pdf-viewer-modal__close"
                        title="Close"
                    >
                        <span className="material-symbols-outlined pdf-viewer-modal__close-icon">
                            close
                        </span>
                    </button>
                </div>
            </div>

            <div
                className="pdf-viewer-modal__body"
                onClick={(e) => e.stopPropagation()}
            >
                {loading ? (
                    <div className="pdf-viewer-modal__status">
                        <span className="material-symbols-outlined pdf-viewer-modal__spinner">
                            progress_activity
                        </span>
                        Loading PDF...
                    </div>
                ) : error ? (
                    <div className="pdf-viewer-modal__status">
                        <span className="material-symbols-outlined pdf-viewer-modal__error-icon">
                            error
                        </span>
                        Failed to load PDF
                    </div>
                ) : (
                    <iframe
                        title={name}
                        src={blobUrl}
                        className="pdf-viewer-modal__frame"
                    />
                )}
            </div>
        </div>,
        document.body,
    );
}
